/**
 * @file        src/pages/offer/OfferList.jsx
 * @description Offer List (Offer Management). Columns: # · Id · Image · Title ·
 *              Service For · Status · Updated On · Action. Search by title /
 *              coupon and filter by service + status.
 * @author      Kinetika Labs
 * @created     2026-07-04
 * @changelog
 *   - 2026-07-04 Claude — Initial (Offer Management).
 *   - 2026-09-15 Claude — Added the one-line note that an offer is only the
 *                          marketing card: the discount comes from the matching
 *                          row in the Coupon List, and an offer whose code has no
 *                          live in-scope coupon is withheld from the storefront
 *                          entirely. Nothing on this screen said so, so a dead
 *                          offer looked like a bug rather than a missing coupon.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import { OFFER_NOTE } from '../../lib/pricingNotes';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Select from '../../components/Select';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get, del } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';

const SERVICE_OPTIONS = [
  { value: '', label: 'All Services' },
  { value: 'Flight', label: 'Flight' },
  { value: 'Hotel', label: 'Hotel' },
  { value: 'Oneway', label: 'Oneway' },
  { value: 'Return', label: 'Return' },
  { value: 'Multicity', label: 'Multicity' },
];

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

export default function OfferList() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [serviceFor, setServiceFor] = useState('');

  const qc = useQueryClient();
  const params = { page, pageSize };
  if (applied) params.q = applied;
  if (serviceFor) params.serviceFor = serviceFor;

  const { data, isLoading } = useQuery({
    queryKey: qk.offers.list(params),
    queryFn: () => get('/offers', { params }),
    placeholderData: keepPreviousData,
  });

  const removeMut = useMutation({
    mutationFn: (id) => del(`/offers/${id}`),
    onSuccess: () => {
      toast.ok('Deleted', 'Offer removed.');
      qc.invalidateQueries({ queryKey: ['offers'] });
    },
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not delete'),
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  function clearFilters() {
    setSearch(''); setApplied(''); setServiceFor(''); setPage(1);
  }

  async function onDelete(row) {
    if (!(await confirm(`Delete offer “${row.title}”?`))) return;
    removeMut.mutate(row.id);
  }

  return (
    <>
      <PageHead
        title="Offer List"
        subtitle="Offer Management"
        actions={<Link to="/offers/new" className="btn primary"><Icon name="plus" /> Add Offer</Link>}
      />

      <div className="notice-banner info">{OFFER_NOTE}</div>

      <article className="panel glass">
        <form className="search-row" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(search); }}>
          <div className="field">
            <label>Title / Coupon</label>
            <input placeholder="Search offer…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="field">
            <label>Service For</label>
            <Select value={serviceFor} onChange={(v) => { setServiceFor(v); setPage(1); }} options={SERVICE_OPTIONS} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Filter</button>
          <button className="btn ghost" type="button" onClick={clearFilters}><Icon name="x" /> Clear Filter</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Id</th><th>Image</th><th>Title</th><th>Service For</th><th>Status</th><th>Updated On</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={8} className="empty">No offers yet. Add your first one.</td></tr>
                ) : rows.map((o, idx) => (
                  <tr key={o.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td className="mono">{o.id}</td>
                    <td>{o.image
                      ? <img src={o.image} alt={o.title} style={{ width: 72, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                      : <span className="muted">—</span>}</td>
                    <td><b>{o.title}</b></td>
                    <td>{o.service_for || <span className="muted">—</span>}</td>
                    <td><Chip tone={o.is_active ? 'ok' : 'rej'}>{o.is_active ? 'Active' : 'Inactive'}</Chip></td>
                    <td>{fmtDate(o.updated_at)}</td>
                    <td className="row-act">
                      <Link to={`/offers/${o.id}/edit`} className="ib edit" aria-label="Edit"><Icon name="edit" /></Link>
                      <button className="ib del" aria-label="Delete" onClick={() => onDelete(o)}><Icon name="trash" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
